import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'
import { addToHistory } from '../utils/history'

type UuidFormat = 'standard' | 'uppercase' | 'no-hyphens' | 'braces' | 'urn'

function UuidGenerator() {
  const { t } = useTranslation()
  const [uuids, setUuids] = useState<string[]>([uuidv4()])
  const [count, setCount] = useState(1)
  const [format, setFormat] = useState<UuidFormat>('standard')
  const [history, setHistory] = useState<string[]>([])

  const saveToToolHistory = useCallback((inputVal: string, outputVal: string) => {
    if (outputVal) {
      addToHistory({
        tool: 'uuid-generator',
        toolName: 'UUID Generator',
        input: inputVal,
        output: outputVal.substring(0, 500),
      })
    }
  }, [])

  const formatUuid = (uuid: string, fmt: UuidFormat): string => {
    switch (fmt) {
      case 'uppercase':
        return uuid.toUpperCase()
      case 'no-hyphens':
        return uuid.replace(/-/g, '')
      case 'braces':
        return `{${uuid}}`
      case 'urn':
        return `urn:uuid:${uuid}`
      default:
        return uuid
    }
  }

  const generateUuids = () => {
    const newUuids: string[] = []
    for (let i = 0; i < count; i++) {
      newUuids.push(uuidv4())
    }
    setUuids(newUuids)
    
    // Add to history (keep last 20)
    setHistory(prev => {
      const updated = [...newUuids, ...prev]
      return updated.slice(0, 20)
    })
    
    // Save to tool history
    saveToToolHistory(`Generate ${count} UUID(s)`, newUuids.join('\n'))
  }

  const handleCountChange = (value: number) => {
    const newCount = Math.max(1, Math.min(100, value))
    setCount(newCount)
  }

  const clearHistory = () => {
    setHistory([])
  }

  const formattedUuids = uuids.map(uuid => formatUuid(uuid, format))
  const allUuidsText = formattedUuids.join('\n')

  const formatOptions: { value: UuidFormat; label: string; example: string }[] = [
    { value: 'standard', label: t('toolPages.uuidGenerator.standard'), example: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
    { value: 'uppercase', label: t('toolPages.uuidGenerator.uppercase'), example: 'XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX' },
    { value: 'no-hyphens', label: t('toolPages.uuidGenerator.noHyphens'), example: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
    { value: 'braces', label: t('toolPages.uuidGenerator.braces'), example: '{xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx}' },
    { value: 'urn', label: t('toolPages.uuidGenerator.urn'), example: 'urn:uuid:xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
  ]

  return (
    <Layout showBackButton title={t('toolPages.uuidGenerator.title')}>
      <div className="tool-container">
        <p className="tool-description">
          {t('toolPages.uuidGenerator.description')}
        </p>

      {/* Controls */}
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('toolPages.uuidGenerator.count')}
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleCountChange(count - 1)}
                className="btn-secondary px-3 py-2"
                disabled={count <= 1}
              >
                -
              </button>
              <input
                type="number"
                value={count}
                onChange={(e) => handleCountChange(parseInt(e.target.value) || 1)}
                min="1"
                max="100"
                className="input w-20 text-center"
              />
              <button
                onClick={() => handleCountChange(count + 1)}
                className="btn-secondary px-3 py-2"
                disabled={count >= 100}
              >
                +
              </button>
            </div>
          </div>

          {/* Quick count buttons */}
          <div className="flex gap-2">
            {[1, 5, 10, 20, 50].map((n) => (
              <button
                key={n}
                onClick={() => {
                  setCount(n)
                }}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                  count === n
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          {/* Generate button */}
          <button onClick={generateUuids} className="btn-primary">
            🎲 {t('common.generate')}
          </button>
        </div>

        {/* Format selection */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('toolPages.uuidGenerator.outputFormat')}
          </label>
          <div className="flex flex-wrap gap-2">
            {formatOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFormat(opt.value)}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                  format === opt.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title={opt.example}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Output Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="section-title">{t('toolPages.uuidGenerator.generatedUuids', { count: formattedUuids.length })}</h2>
            <CopyButton text={allUuidsText} />
          </div>
          
          <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin">
            {formattedUuids.map((uuid, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg group"
              >
                <code className="font-mono text-sm text-gray-800 dark:text-gray-200 break-all">
                  {uuid}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(uuid)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-2 p-1 text-gray-500 hover:text-primary-600"
                  title={t('common.copy')}
                >
                  📋
                </button>
              </div>
            ))}
          </div>

          {formattedUuids.length > 1 && (
            <div className="mt-4">
              <textarea
                value={allUuidsText}
                readOnly
                className="textarea h-32 bg-gray-50 dark:bg-gray-900 scrollbar-thin font-mono text-sm"
              />
            </div>
          )}
        </div>

        {/* History Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="section-title">{t('toolPages.uuidGenerator.recentHistory')}</h2>
            {history.length > 0 && (
              <button onClick={clearHistory} className="btn-secondary text-sm">
                {t('common.clear')}
              </button>
            )}
          </div>
          
          {history.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {t('toolPages.uuidGenerator.noHistory')}
            </p>
          ) : (
            <div className="space-y-1 max-h-96 overflow-y-auto scrollbar-thin">
              {history.map((uuid, index) => (
                <div
                  key={`${uuid}-${index}`}
                  className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded group"
                >
                  <code className="font-mono text-xs text-gray-600 dark:text-gray-400 break-all">
                    {uuid}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(uuid)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity ml-2 p-1 text-gray-500 hover:text-primary-600"
                    title={t('common.copy')}
                  >
                    📋
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* UUID Validator */}
      <div className="mt-6 card">
        <h2 className="section-title mb-3">{t('toolPages.uuidGenerator.uuidValidation')}</h2>
        <UuidValidator />
      </div>

      {/* Info Section */}
      <div className="mt-8 card bg-gray-50 dark:bg-gray-800/50">
        <h3 className="font-semibold mb-2">💡 {t('toolPages.uuidGenerator.infoTitle')}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {t('toolPages.uuidGenerator.infoDesc')}
        </p>
        <h4 className="font-medium text-sm mb-1">{t('toolPages.uuidGenerator.uuidV4Features')}</h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li>{t('toolPages.uuidGenerator.randomGeneration')}</li>
          <li>{t('toolPages.uuidGenerator.bitInfo')}</li>
          <li>{t('toolPages.uuidGenerator.formatInfo')}</li>
          <li>{t('toolPages.uuidGenerator.example')}</li>
        </ul>
      </div>
      </div>
    </Layout>
  )
}

// UUID Validator Component
function UuidValidator() {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const [result, setResult] = useState<{ valid: boolean; version?: number; message: string } | null>(null)

  const validateUuid = (value: string) => {
    setInput(value)
    
    if (!value.trim()) {
      setResult(null)
      return
    }

    // Remove common formats
    let cleaned = value.trim()
    if (cleaned.startsWith('{') && cleaned.endsWith('}')) {
      cleaned = cleaned.slice(1, -1)
    }
    if (cleaned.startsWith('urn:uuid:')) {
      cleaned = cleaned.slice(9)
    }

    // Standard UUID regex
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    const uuidNoHyphenRegex = /^[0-9a-f]{32}$/i

    let isValid = false
    let version: number | undefined

    if (uuidRegex.test(cleaned)) {
      isValid = true
      version = parseInt(cleaned.charAt(14), 16)
    } else if (uuidNoHyphenRegex.test(cleaned)) {
      isValid = true
      version = parseInt(cleaned.charAt(12), 16)
    }

    if (isValid && version) {
      setResult({
        valid: true,
        version,
        message: t('toolPages.uuidGenerator.validUuid', { version })
      })
    } else {
      setResult({
        valid: false,
        message: t('toolPages.uuidGenerator.invalidUuid')
      })
    }
  }

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={input}
        onChange={(e) => validateUuid(e.target.value)}
        placeholder={t('toolPages.uuidGenerator.validationPlaceholder')}
        className="input font-mono"
      />
      {result && (
        <div className={`p-3 rounded-lg ${
          result.valid 
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
        }`}>
          <p className={`text-sm ${
            result.valid 
              ? 'text-green-700 dark:text-green-400'
              : 'text-red-700 dark:text-red-400'
          }`}>
            {result.valid ? '✅' : '❌'} {result.message}
          </p>
        </div>
      )}
    </div>
  )
}

export default UuidGenerator
